import React from 'react';
import {
  TextField,
  Slider,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
  Tooltip,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ACTIVATION_FUNCTIONS } from '../utils/constants';

/**
 * Slider control with label and optional tooltip
 */
export const SliderControl = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  tooltip,
  disabled = false,
  marks = false,
  valueLabelFormat,
  valueDisplay,
}) => (
  <Box sx={{ mb: 2, width: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
        {valueDisplay ? `: ${valueDisplay}` : value !== undefined ? `: ${value}` : ''}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <Slider
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      min={min}
      max={max}
      step={step}
      valueLabelDisplay="auto"
      valueLabelFormat={valueLabelFormat}
      disabled={disabled}
      marks={marks === true ? [
        { value: min, label: `${min}` },
        { value: max, label: `${max}` },
      ] : marks}
    />
  </Box>
);

/**
 * Select control with label and optional tooltip
 */
export const SelectControl = ({
  value,
  onChange,
  options,
  label,
  tooltip,
  disabled = false,
  fullWidth = true,
  size = "small",
  required = false,
  helperText,
}) => (
  <Box sx={{ mb: 2, width: '100%' }}>
    <FormControl fullWidth={fullWidth} size={size} required={required}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <InputLabel id={`select-label-${label}`}>{label}</InputLabel>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Select
        labelId={`select-label-${label}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label}
        disabled={disabled}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  </Box>
);

/**
 * Switch control with label and optional tooltip
 */
export const SwitchControl = ({
  checked,
  onChange,
  label,
  tooltip,
  disabled = false,
}) => (
  <Box sx={{ mb: 1.5, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      size="small"
    />
  </Box>
);

/**
 * Text field control with label and optional tooltip
 */
export const TextFieldControl = ({
  value,
  onChange,
  label,
  tooltip,
  disabled = false,
  multiline = false,
  rows = 1,
  type = "text",
  fullWidth = true,
  required = false,
  helperText,
  size = "small",
}) => (
  <Box sx={{ mb: 2, width: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {tooltip && (
        <Tooltip title={tooltip} arrow>
          <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      multiline={multiline}
      rows={rows}
      type={type}
      fullWidth={fullWidth}
      required={required}
      helperText={helperText}
      size={size}
    />
  </Box>
);

/**
 * Activation function selector component
 */
export const ActivationSelector = ({
  value,
  onChange,
  label = "Activation Function",
  disabled = false,
}) => (
  <SelectControl
    value={value}
    onChange={onChange}
    label={label}
    options={ACTIVATION_FUNCTIONS.map(fn => ({ value: fn, label: fn.charAt(0).toUpperCase() + fn.slice(1) }))}
    tooltip="The activation function to use for this layer"
    disabled={disabled}
  />
);

/**
 * Shape input component (for input layer dimensions)
 */
export const ShapeInput = ({
  value,
  onChange,
  label = "Input Shape",
  tooltip = "The shape of the input tensor, excluding the batch dimension",
  disabled = false,
}) => {
  const handleChange = (index, newValue) => {
    const newShape = [...value];
    newShape[index] = parseInt(newValue) || null;
    onChange(newShape);
  };

  const addDimension = () => {
    onChange([...value, 1]);
  };

  const removeDimension = () => {
    if (value.length > 1) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <Grid container spacing={1}>
        {value.map((dim, index) => (
          <Grid item xs={4} key={index}>
            <TextField
              value={dim === null ? '' : dim}
              onChange={(e) => handleChange(index, e.target.value)}
              disabled={disabled}
              size="small"
              type="number"
              label={`Dim ${index}`}
              fullWidth
            />
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ display: 'flex', mt: 1, gap: 1 }}>
        <Chip 
          label="Add Dimension" 
          size="small" 
          onClick={addDimension} 
          disabled={disabled || value.length >= 4}
        />
        <Chip 
          label="Remove Dimension" 
          size="small" 
          onClick={removeDimension} 
          disabled={disabled || value.length <= 1}
        />
      </Box>
    </Box>
  );
};

/**
 * Number array input component (for kernel size, strides, etc.)
 */
export const NumberArrayInput = ({
  value,
  onChange,
  label,
  dimensions = 2,
  tooltip,
  disabled = false,
}) => {
  const handleChange = (index, newValue) => {
    const newArray = [...value];
    newArray[index] = parseInt(newValue) || 0;
    onChange(newArray);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip} arrow>
            <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <Grid container spacing={1}>
        {Array.from({ length: dimensions }).map((_, index) => (
          <Grid item xs={6} key={index}>
            <TextField
              value={value[index] || 0}
              onChange={(e) => handleChange(index, e.target.value)}
              disabled={disabled}
              size="small"
              type="number"
              label={dimensions === 1 ? '' : (index === 0 ? 'Width' : 'Height')}
              fullWidth
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

/**
 * Dynamic controls generator component based on TensorFlow parameter definitions
 * Takes a parameter definition object and renders the appropriate UI controls
 */
export const DynamicControls = ({
  parameters,
  values,
  onChange,
  filter = null, // Optional filter function to determine which parameters to show
}) => {
  // If filter function is provided, filter parameters
  const filteredParams = filter 
    ? Object.entries(parameters).filter(([key, _]) => filter(key))
    : Object.entries(parameters);
  
  return (
    <>
      {filteredParams.map(([key, paramConfig]) => {
        const currentValue = values[key] !== undefined ? values[key] : paramConfig.default;
        
        // Common props for all controls
        const commonProps = {
          key,
          label: key.charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1), // Convert snake_case to Title Case
          tooltip: paramConfig.description,
        };
        
        // Based on the parameter type, render the appropriate control
        switch(paramConfig.type) {
          case 'number':
            return (
              <SliderControl
                {...commonProps}
                value={currentValue || 0}
                onChange={(value) => onChange(key, value)}
                min={paramConfig.min || 1}
                max={paramConfig.max || 512}
                step={paramConfig.step || 1}
                marks
              />
            );
            
          case 'float':
            return (
              <SliderControl
                {...commonProps}
                value={currentValue || 0}
                onChange={(value) => onChange(key, value)}
                min={paramConfig.range ? paramConfig.range[0] : 0}
                max={paramConfig.range ? paramConfig.range[1] : 1}
                step={0.05}
                marks
                valueDisplay={key === 'rate' ? `${Math.round((currentValue || 0) * 100)}%` : undefined}
              />
            );
            
          case 'boolean':
            return (
              <SwitchControl
                {...commonProps}
                checked={currentValue !== undefined ? currentValue : paramConfig.default || false}
                onChange={(value) => onChange(key, value)}
              />
            );
            
          case 'activation':
            return (
              <ActivationSelector
                {...commonProps}
                value={currentValue || paramConfig.default || 'relu'}
                onChange={(value) => onChange(key, value)}
              />
            );
            
          case 'select':
            return (
              <SelectControl
                {...commonProps}
                value={currentValue || paramConfig.default}
                onChange={(value) => onChange(key, value)}
                options={paramConfig.options.map(option => ({
                  value: option,
                  label: option === null ? 'None' : option.charAt(0).toUpperCase() + option.slice(1)
                }))}
              />
            );
            
          case 'initializer':
            return (
              <SelectControl
                {...commonProps}
                value={currentValue || paramConfig.default || 'glorot_uniform'}
                onChange={(value) => onChange(key, value)}
                options={[
                  { value: 'glorot_uniform', label: 'Glorot Uniform' },
                  { value: 'glorot_normal', label: 'Glorot Normal' },
                  { value: 'he_uniform', label: 'He Uniform' },
                  { value: 'he_normal', label: 'He Normal' },
                  { value: 'zeros', label: 'Zeros' },
                  { value: 'ones', label: 'Ones' },
                  { value: 'random_normal', label: 'Random Normal' },
                  { value: 'random_uniform', label: 'Random Uniform' },
                  { value: 'orthogonal', label: 'Orthogonal' },
                ]}
              />
            );
            
          case 'regularizer':
            return (
              <SelectControl
                {...commonProps}
                value={currentValue || 'null'}
                onChange={(value) => onChange(key, value === 'null' ? null : value)}
                options={[
                  { value: 'null', label: 'None' },
                  { value: 'l1', label: 'L1' },
                  { value: 'l2', label: 'L2' },
                  { value: 'l1_l2', label: 'L1 L2' },
                ]}
              />
            );
            
          case 'constraint':
            return (
              <SelectControl
                {...commonProps}
                value={currentValue || 'null'}
                onChange={(value) => onChange(key, value === 'null' ? null : value)}
                options={[
                  { value: 'null', label: 'None' },
                  { value: 'max_norm', label: 'Max Norm' },
                  { value: 'min_max_norm', label: 'Min Max Norm' },
                  { value: 'non_neg', label: 'Non Negative' },
                  { value: 'unit_norm', label: 'Unit Norm' },
                ]}
              />
            );
            
          case 'shape':
            return (
              <NumberArrayInput
                {...commonProps}
                value={currentValue || [null, null]}
                onChange={(value) => onChange(key, value)}
                dimensions={2}
              />
            );
            
          case 'integer':
          default:
            return (
              <SliderControl
                {...commonProps}
                value={currentValue || 0}
                onChange={(value) => onChange(key, parseInt(value))}
                min={paramConfig.min || 0}
                max={paramConfig.max || 1000}
                step={1}
              />
            );
        }
      })}
    </>
  );
}; 